import React, { useState, useEffect, useRef, useCallback } from "react";

const PROFILES = [
  {
    id: 1,
    name: "Aarav",
    age: 24,
    bio: "Coffee lover ☕ | Night rides & deep conversations 🌙",
    image:
      "https://i.pinimg.com/1200x/c0/d4/c6/c0d4c6b37bea77075b13f35b102abbcf.jpg",
  },
  {
    id: 2,
    name: "Ananya",
    age: 23,
    bio: "Creative soul 🎨 | Travel, sunsets & good vibes ✨",
    image: "https://wallpapercave.com/wp/wp6893154.jpg",
  },
  {
    id: 3,
    name: "Riya",
    age: 25,
    bio: "Fitness, fashion & spontaneous plans 💃🔥",
    image:
      "https://i.pinimg.com/originals/f3/8d/bc/f38dbcbab1e30643e05f5b9caa8aaa39.jpg",
  },
];

const AUTO_SWIPE_TIME = 5000;
const EXIT_DISTANCE = 500;
const EXIT_DURATION = 300;
const PROMOTE_DURATION = 2000;

export default function MatchSwipeCard() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [exitDir, setExitDir] = useState(null);
  const [isPromoting, setIsPromoting] = useState(false);

  const timerRef = useRef(null);

  const current = PROFILES[index];

  /* ---------------- PREVIEW INDEX ---------------- */

  const getNextIndex = () => {
    const candidate = index + direction;

    if (candidate >= 0 && candidate < PROFILES.length) {
      return candidate;
    }

    return index - direction;
  };

  const next = PROFILES[getNextIndex()];

  /* ---------------- AUTO SWIPE ---------------- */

  const autoSwipe = useCallback(() => {
    setExitDir(direction);
  }, [direction]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSwipe, AUTO_SWIPE_TIME);
    return () => clearTimeout(timerRef.current);
  }, [index, autoSwipe]);

  /* ---------------- EXIT → PROMOTE (FIXED) ---------------- */

  useEffect(() => {
    if (exitDir === null) return;

    const timer = setTimeout(() => {
      setIsPromoting(true);

      setIndex((prev) => {
        const last = PROFILES.length - 1;

        // ➡️ moving forward
        if (direction === 1) {
          if (prev < last) {
            return prev + 1;
          }
          // reached end → flip direction
          setDirection(-1);
          return prev - 1;
        }

        // ⬅️ moving backward
        if (direction === -1) {
          if (prev > 0) {
            return prev - 1;
          }
          // reached start → flip direction
          setDirection(1);
          return prev + 1;
        }

        return prev;
      });

      setExitDir(null);

      setTimeout(() => {
        setIsPromoting(false);
      }, PROMOTE_DURATION);
    }, EXIT_DURATION);

    return () => clearTimeout(timer);
  }, [exitDir, direction]);

  /* ---------------- BUTTONS ---------------- */

  const like = () => {
    if (exitDir !== null) return;
    setExitDir(1);
  };

  const reject = () => {
    if (exitDir !== null) return;
    setExitDir(-1);
  };

  /* ---------------- STYLES ---------------- */

  const activeStyle =
    exitDir !== null
      ? {
          transform: `translateX(${exitDir * EXIT_DISTANCE}px)`,
          opacity: 0,
          transition: `transform ${EXIT_DURATION}ms ease-out, opacity ${EXIT_DURATION}ms ease-out`,
        }
      : {
          transform: "translateX(0)",
          opacity: 1,
          transition: "none",
        };

  const previewStyle = isPromoting
    ? {
        transform: "scale(1)",
        opacity: 1,
        transition: `transform ${PROMOTE_DURATION}ms ease, opacity ${PROMOTE_DURATION}ms ease`,
      }
    : {
        transform: "scale(0.96)",
        opacity: 0.2,
        transition: "none",
      };

  return (
    <div className="relative w-full max-w-[420px] mx-auto h-[550px]">
      {/* BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-pink-300 rounded-[40px] -rotate-6 scale-95 opacity-40 pointer-events-none" />

      {/* PREVIEW CARD */}
      <div
        className="absolute inset-0 rounded-[35px] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.2)]"
        style={{ ...previewStyle, zIndex: 1 }}
      >
        <div
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(0,0,0,.25), rgba(0,0,0,.4)), url(${next.image})`,
          }}
        />
      </div>

      {/* ACTIVE CARD */}
      <div
        key={current.id}
        className="absolute inset-0 bg-white rounded-[35px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
        style={{ ...activeStyle, zIndex: 2 }}
      >
        <div
          className="w-full h-full bg-cover bg-center relative"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(0,0,0,.3), rgba(0,0,0,.5)), url(${current.image})`,
          }}
        >
          {/* Indicator */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {PROFILES.map((_, i) => (
              <div
                key={i}
                className={`h-1 w-12 rounded-full ${
                  i === index ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>

          {/* Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 translate-y-[25%]">
            <h2 className="text-4xl font-bold text-white">
              {current.name}, {current.age}
            </h2>
            <p className="text-white/90 max-w-xs mt-1 mb-2">{current.bio}</p>
            <div className="pt-1 border-t border-white/30">
              <p className="text-2xl font-bold text-white">Join Vibely</p>
              <p className="text-sm text-white/85">
                Swipe to find your perfect match
              </p>
            </div>
          </div>

          <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
      </div>

      {/* ACTION BUTTONS */}
      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex gap-8 z-10">
        <button
          onClick={reject}
          disabled={exitDir !== null}
          className="w-20 h-20 rounded-full bg-white border-4 border-[#ff6b6b] shadow-xl hover:scale-110 active:scale-95 transition"
        >
          <span className="text-4xl text-[#ff6b6b]">✕</span>
        </button>

        <button
          onClick={like}
          disabled={exitDir !== null}
          className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-xl hover:scale-110 active:scale-95 transition"
        >
          <span className="text-4xl">♥</span>
        </button>
      </div>
    </div>
  );
}
